<template>
  <div class="ui-filters">
    <ElPopover
      v-model:visible="filtersOpen"
      placement="bottom"
      trigger="manual"
      width="325"
      popper-class="ui-filters_wrapper"
    >
      <template v-slot:reference>
        <div
          ref="mainBtn"
          v-click-outside="handleClickOutside"
          class="btn btn-refresh btn-sm !tw-flex"
          :class="{ 'btn-primary': filtersOpen || activeFilter,
                    'btn-outline-primary': !filtersOpen && !activeFilter }"
          @click="toggleFilters"
        >
          <i class="fas fa-filter mr-4" />
          {{ $t('ui.filters.inner.filters') }}
          <span
            v-if="activeFilter"
            class="length-filter pl-2"
          >({{ filterLength }})</span>
        </div>
      </template>

      <div
        ref="pop"
        class="ui-filters_pop"
      >
        <div
          v-if="isMobile"
          class="ui-filters_title"
        >
          <div
            class="cross"
            @click="toggleFilters"
          >
            ×
          </div>{{ $t('filters.filter') }}
          <div
            v-if="activeFilter && isMobile"
            class="reset-filters text-red ml-8"
            @click="resetFilters"
          >
            {{ $t('filters.resetFilters') }}
          </div>
        </div>
        <UiSelect
          v-for="(item, id) in currentFilter"
          :key="id"
          v-model="item.value"
          :label="item.title"
          :list="item.options"
          :empty-values="[undefined]"
          :value-on-clear="undefined"
          is-search
          type="filter"
          :teleported="false"
          :multiple="item.multiple"
        />
        <div class="!tw-flex pl-16 pr-16 mb-16 pt-16 filter-buttons">
          <button
            :disabled="!activeFilter"
            class="btn btn-primary btn-refresh !tw-w-full"
            @click="applyFilters()"
          >
            <span class="before-icon">{{ $t('filters.applyFilters') }}</span>
          </button>
          <button
            v-if="activeFilter && !isMobile"
            class="btn btn-outline-danger btn-sm ml-8 !tw-w-full"
            @click="resetFilters"
          >
            <span class="text-red">×</span>
            {{ $t('filters.resetFilters') }}
          </button>
        </div>
      </div>
    </ElPopover>
    <div
      v-if="activeFilter && !isMobile"
      class="reset-filters text-red ml-8"
      @click="resetFilters"
    >
      {{ $t('filters.resetFilters') }}
    </div>
  </div>
</template>

<script>

import ResponsiveMixin from '@/components/Helpers/responsive-mixin';

export default {
  name: 'UiFilters',

  mixins: [
    ResponsiveMixin,
  ],

  props: {
    useFilters: {
      type: Array,
      default() {
        return [
          'traffic_source_id',
          'country_code',
          'player_id',
          'affiliate_offer_id',
          'subid1',
          'subid2',
          'subid3',
          'subid4',
          'subid5',
        ];
      },
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    route: {
      type: String,
      default: () => (''),
    },
  },
  emits: ['input', 'reset'],
  data() {
    return {
      filtersOpen: false,
      allFilters: {},
    };
  },

  watch: {
    allFiltersMeta: {
      immediate: true,
      deep: true,
      handler(value) {
        this.allFilters = {
          ...this.allFilters,
          ...value,
        };
      },
    },
  },

  computed: {
    moderationStatusList() {
      return ['', 'on_review', 'for_rework', 'approved', 'declined'].map((type) => ({
        id: type,
        text: this.$t(`mediaBuying.payments.status.${type === '' ? 'all_moderation_status' : type}`),
      }));
    },

    payoutStatusList() {
      return ['', 'not_paid', 'for_rework', 'processing', 'paid', 'failed'].map((type) => ({
        id: type,
        text: this.$t(`mediaBuying.payments.status.${type === '' ? 'all_payout_status' : type}`),
      }));
    },

    statusFilters() {
      return ['', 'draft', 'on_review', 'for_rework', 'approved', 'declined'].map((type) => ({
        id: type,
        text: this.$t(`mediaBuying.posts.status.${type === '' ? 'all_status' : type}`),
      }));
    },

    typeFilters() {
      return ['', 'publication', 'cancellation'].map((type) => ({
        id: type,
        text: this.$t(`mediaBuying.posts.type.${type === '' ? 'all_type' : type}`),
      }));
    },

    allFiltersMeta() {
      return {
        typePosts: {
          type: 'typePosts',
          field: 'typePosts',
          value: '',
          title: this.$t('filters.type.title'),
          options: this.typeFilters,
        },

        statusPosts: {
          type: 'statusPosts',
          field: 'statusPosts',
          value: '',
          title: this.$t('postbacks.card.status'),
          options: this.statusFilters,
        },

        moderationStatus: {
          type: 'moderationStatus',
          field: 'moderationStatus',
          value: '',
          title: this.$t('mediaBuying.payments.status.moderation_status'),
          options: this.moderationStatusList,
        },

        payoutStatus: {
          type: 'payoutStatus',
          field: 'payoutStatus',
          value: '',
          title: this.$t('mediaBuying.payments.status.payout_status'),
          options: this.payoutStatusList,
        },
      };
    },

    languages() {
      const languages = this.$store.state.misc.languages.map((l) => ({
        id: l.code,
        text: l.name,
      }));

      return [{
        id: 'all',
        text: this.$t('filters.language.all'),
      }, ...languages];
    },
    brands() {
      const brands = this.$store.state.media.sites.map((e) => ({
        id: e.id,
        text: e.name,
      }));
      return [{
        id: 'all',
        text: this.$t('filters.brand.all'),
      }, ...brands];
    },
    currentFilter() {
      return this.initFilters();
    },
    filterLength() {
      return this.currentFilter.filter((e) => e.value !== '' && e.value !== 'all').length;
    },

    activeFilter() {
      return this.filterLength > 0;
    },
  },

  methods: {
    initFilters() {
      const filteredArray = this.$_.filter(this.allFilters, (item) => this.useFilters.indexOf(item.type) !== -1);
      filteredArray.forEach((item) => {
        if (this.filters !== null) {
          item.value = this.filters[item.field];
        } else {
          item.value = '';
        }
      });
      return filteredArray;
    },
    handleClickOutside(event) {
      const triggerEl = this.$refs.pop;
      const popoverEl = document.querySelector('.el-popover');
      if (
        triggerEl &&
        !triggerEl.contains(event.target) &&
        !popoverEl?.contains(event.target)
      ) {
        this.filtersOpen = false;
        this.initFilters();
      }
    },
    toggleFilters() {
      this.filtersOpen = !this.filtersOpen;
      this.initFilters();
    },

    initDictionaries() {
      this.allFilters.brand.options = this.brands;
      this.allFilters.language.options = this.languages;
    },

    applyFilters() {
      const val = {};
      this.currentFilter.forEach((filter) => {
        this.$_.assign(val, { [filter.field]: filter.value });
      });
      this.$emit('input', val);
      this.filtersOpen = false;
    },
    resetFilters() {
      this.$emit('reset');
      this.initFilters();
      this.filtersOpen = false;
    },
  },
};
</script>


<style lang="scss">
  // vue3-migration -- '@/assets/theme/common-page-styles.scss'
  // @import 'src/assets/theme/default/components/UiFilters';
</style>
